import { format } from 'date-fns';
import { getI18n } from 'react-i18next';

import { BpType, SyncState } from '../../enums';
import { IPatient } from '../../interfaces';
import { toUTCDate } from '../../utils';

export const getPatientName = (patient?: IPatient) => {
  return `${patient?.lastName.toUpperCase()}, ${patient?.firstName}`;
};

export const getPatientSyncState = (patient: IPatient) => {
  const { nextInitialization, lastDateForMeasurements } = patient.bloodPressure;
  if (!nextInitialization || !lastDateForMeasurements) {
    return SyncState.CONNECTED;
  }

  if (patient.stopSharingDate) {
    return SyncState.DISCONNECTED;
  }

  const now = toUTCDate(Date.now()).getTime();
  const delta = nextInitialization - now;
  if (delta > 0) {
    // initialization is valid if next initialization is due in more than 5 days
    const validDelta = 5 * 24 * 60 * 60 * 1000;
    if (delta > validDelta) {
      return SyncState.VALID;
    } else {
      return SyncState.WARNING;
    }
  } else {
    if (lastDateForMeasurements > now) {
      return SyncState.ALERT;
    } else {
      return SyncState.ERROR;
    }
  }
};

export const getPatientSyncStateText = (patient: IPatient) => {
  const syncState = getPatientSyncState(patient);
  const key = `common.syncState.title.${syncState}`;
  if (syncState !== SyncState.VALID) {
    return getI18n().t(key);
  }

  const date = format(toUTCDate(patient.bloodPressure.lastSync ?? 0), 'd MMM, h:mmaaa');
  return getI18n().t(key, { date });
};

export const getPatientBpState = (status: BpType, isUS: boolean) =>
  status === BpType.STAGE_3 && isUS ? BpType.HYPERTENSIVE_CRISIS : status;

export const getPatientBpStateText = (status: BpType, isUS: boolean) =>
  getI18n().t(`common.bpType.${getPatientBpState(status, isUS)}`);
